<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()
const showUserMenu = ref(false)

const maskedPhone = computed(() => {
  if (!authStore.phone) return ''
  return authStore.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
})

const navItems = [
  { name: '应用领域', path: '/applications' },
  { name: '招募合作', path: '/join' },
  { name: '关于我们', path: '/about-us' }
]

const menuGroups = [
  {
    title: '业务工具',
    items: [
      { name: '样品申请', path: '/sample/list' },
      { name: '佣金计算', path: '/commission/calc' }
    ]
  },
  {
    title: '客户与收益',
    items: [
      { name: '邀请中心', path: '/invite' },
      { name: '我的收益', path: '/earnings' },
      { name: '成交订单', path: '/earnings/deals' },
      { name: '我的客户', path: '/customers' }
    ]
  },
  {
    title: '账号设置',
    items: [
      { name: '收货地址', path: '/addresses' },
      { name: '账号设置', path: '/account-settings' }
    ]
  }
]

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
}

function closeUserMenu() {
  showUserMenu.value = false
}

function openUserMenu() {
  showUserMenu.value = true
}

function handleLogout() {
  authStore.logout()
  closeUserMenu()
  router.push('/login')
}
</script>

<template>
  <header class="header">
    <div class="container header-inner">
      <router-link to="/applications" class="logo">
        <div class="logo-icon">
          <img src="@/assets/logo.png" alt="水芯" class="logo-img" />
        </div>
        <span class="brand-cn">水芯</span>
      </router-link>

      <nav class="nav-desktop" aria-label="主导航">
        <router-link
          v-for="item in navItems"
          :key="item.name"
          :to="item.path"
          class="nav-link"
          active-class="active-link"
        >
          {{ item.name }}
        </router-link>
      </nav>

      <div class="header-actions">
        <div v-if="authStore.isLoggedIn" class="user-dropdown-container" @mouseenter="openUserMenu" @mouseleave="closeUserMenu">
          <button type="button" class="user-account-btn" @click="toggleUserMenu">
            <span class="user-avatar">我</span>
            <span>{{ maskedPhone }}</span>
          </button>

          <div v-if="showUserMenu" class="dropdown-menu">
            <div v-for="group in menuGroups" :key="group.title" class="dropdown-group">
              <div class="dropdown-group-title">{{ group.title }}</div>
              <router-link
                v-for="item in group.items"
                :key="item.path"
                :to="item.path"
                class="dropdown-item"
                @click="closeUserMenu"
              >
                {{ item.name }}
              </router-link>
            </div>
            <button type="button" class="dropdown-item logout-item" @click="handleLogout">
              退出登录
            </button>
          </div>
        </div>

        <div v-else class="auth-links">
          <router-link to="/login" class="auth-link">登录</router-link>
          <span class="divider">/</span>
          <router-link to="/register" class="auth-link">注册</router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  background-color: #ffffff;
  border-bottom: 1px solid #e6ecf2;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(16, 32, 51, 0.04);
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  padding: 0 20px;
}

.logo {
  display: flex;
  align-items: center;
  color: #1a1a1a;
  text-decoration: none;
}

.logo-icon {
  width: 40px;
  height: 40px;
  margin-right: 12px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 2px;
}

.brand-cn {
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 1px;
}

.nav-desktop {
  display: flex;
  gap: 25px;
  margin-left: 40px;
  margin-right: auto;
}

.nav-link {
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  padding: 8px 0;
  border-bottom: 2px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;
}

.nav-link:hover,
.active-link {
  color: #005eb8;
  border-bottom-color: #005eb8;
}

.header-actions {
  display: flex;
  align-items: center;
}

.auth-links {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #555;
  font-size: 13px;
  font-weight: 600;
}

.auth-link {
  color: #1a1a1a;
  text-decoration: none;
}

.auth-link:hover {
  color: #005eb8;
  text-decoration: underline;
}

.divider {
  color: #c8d0d8;
  margin: 0 5px;
}

.user-dropdown-container {
  position: relative;
}

.user-account-btn {
  min-height: 38px;
  border: 1px solid #cfe4f8;
  border-radius: 999px;
  background: #f0f8ff;
  color: #005eb8;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px 5px 6px;
  font-size: 13px;
  font-weight: 700;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.user-account-btn:hover {
  background: #e5f3ff;
  border-color: #9dc8ee;
  color: #004a94;
}

.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #005eb8;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 1001;
  min-width: 220px;
  padding: 10px;
  background: #ffffff;
  border: 1px solid #e3e9ef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 32, 51, 0.12);
  animation: slideDown 0.16s ease-out;
}

.dropdown-menu::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  height: 8px;
}

.dropdown-group {
  padding: 4px 0 8px;
  border-bottom: 1px solid #edf1f5;
}

.dropdown-group + .dropdown-group {
  padding-top: 8px;
}

.dropdown-group-title {
  padding: 4px 10px 6px;
  color: #7a8794;
  font-size: 12px;
  font-weight: 700;
}

.dropdown-item {
  width: 100%;
  display: block;
  padding: 9px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  text-align: left;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.dropdown-item:hover {
  background: #f0f8ff;
  color: #005eb8;
}

.logout-item {
  margin-top: 8px;
  color: #c62828;
}

.logout-item:hover {
  background: #fff2f0;
  color: #a61d24;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 720px) {
  .header-inner {
    height: 64px;
    padding: 0 16px;
  }

  .logo-icon {
    width: 38px;
    height: 38px;
    margin-right: 8px;
  }

  .nav-desktop {
    display: none;
  }

  .dropdown-menu {
    right: -8px;
  }
}

@media (max-width: 420px) {
  .brand-cn {
    display: none;
  }
}
</style>
